<template>
  <div class="content-container" v-if="vehicleData">
    <!-- Panel boczny z informacjami o pojeździe -->
    <div class="side-info">
      <div v-if="!isSG" class="mini-map-section">
        <span>{{ t('measurement-location') }}</span>
        <div class="mini-map-wrapper">
          <div ref="miniMapContainer" class="mini-map-container"></div>
          <p v-if="!hasMiniMapPoint" class="mini-map-no-data">
            {{ t('no-coordinates-for-location') }}
          </p>
        </div>
      </div>
      <!-- Data i godzina rejestracji pojazdu -->
      <div class="date-time">
        <span>{{ t('registration-at-preselection-station') }}</span>
        <div class="d-flex">
          <div class="fw-bold date">
            {{ moment(vehicleData.createdAt).format("DD.MM.YYYY") }}
          </div>
          <div class="fw-bold time">
            {{ moment(vehicleData.createdAt).format("HH:mm:ss") }}
          </div>
        </div>
      </div>
      <!-- Lokalizacja pojazdu -->
      <div class="location">
        <span>{{ t('preselection-station') }}</span>
        <div class="fw-bold">
          {{ vehicleData.location?.shortName }}
        </div>
      </div>
      <!-- Numer rejestracyjny i flaga kraju -->
      <!-- Pasek tablicy pokazujemy tylko, gdy jest co pokazać — przy braku odczytu
           LPR i kraju zostawał pusty biały pas na całą szerokość panelu. -->
      <div class="row plate-item" v-if="vehicleData.plateLpr || getCode(vehicleData.nationality)">
        <div class="col-3 plate-flag center-aligned"
        :style="{ backgroundColor: getCode(vehicle.nationality) ? '' : 'white'}">
          <div class="row">
            <div>
              <country-flag
                :country="getCode(vehicleData.nationality)"
                size="small"
              />
            </div>
          </div>
          <div class="row">
            <div>{{ getCode(vehicle.nationality) ? getCode(vehicle.nationality) : "" }}</div>
          </div>
        </div>
        <div class="col-9 plate">{{ vehicleData.plateLpr }}</div>
      </div>
      <!-- Pozostałe informacje (pas, kategorie, numer pojazdu) -->
      <div class="info d-flex">
        <div class="label">{{ t('lane-number') }}:</div>
        <div class="fw-bold value">
          {{ vehicleData.lane?.laneNumber }}
        </div>
      </div>
      <div v-if="!isSG" class="info d-flex">
        <div class="label">{{ t('category') }} [8+1]:</div>
        <div class="fw-bold value">{{ vehicleData.ctg8p1 }}</div>
      </div>
      <div v-if="!isSG" class="info d-flex">
        <div class="label">{{ t('category') }} [COST 323]:</div>
        <div class="fw-bold value">{{ vehicleData.ctg323 }}</div>
      </div>
      <div class="info d-flex">
        <div class="label">{{ t('another-vehicle-number') }}:</div>
        <div class="fw-bold value">{{ vehicleData.id }}</div>
      </div>

      <!-- Normy EURO idą osobnym żądaniem do zewnętrznej usługi (czas odpowiedzi poza naszą
           kontrolą), więc w miejscu docelowych danych pokazujemy spinner do czasu odpowiedzi. -->
      <div class="info d-flex" v-if="euroNormsLoading">
        <div class="label">{{ t('euro-norms') }}:</div>
        <div class="fw-bold value euro-norms-loading">
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span class="euro-norms-loading__text">{{ t('loading') }}</span>
        </div>
      </div>

      <div class="info d-flex" v-if="euroNorms && euroNorms.make">
        <div class="label">{{ t('vehicle-make') }}:</div>
        <div class="fw-bold value">{{ euroNorms.make ?? '' }}</div>
      </div>
      <div class="info d-flex" v-if="euroNorms && euroNorms.model">
        <div class="label">{{ t('vehicle-model') }}:</div>
        <div class="fw-bold value">{{ euroNorms.model ?? '' }}</div>
      </div>
      <div class="info d-flex" v-if="euroNorms && euroNorms.veh_type && euroNorms.veh_type !== '---'">
        <div class="label">{{ t('catalog-no') }}:</div>
        <div class="fw-bold value">{{ euroNorms.veh_type ?? '' }}</div>
      </div>
      <div class="info d-flex" v-if="euroNorms && euroNorms.euro_norm">
        <div class="label">{{ t('euro-norm') }}:</div>
        <div class="fw-bold value">{{ euroNorms.euro_norm ?? '' }}</div>
      </div>
      <div class="info d-flex" v-if="euroNorms && euroNorms.production_date">
        <div class="label">{{ t('production-date') }}:</div>
        <div class="fw-bold value">{{ euroNorms.production_date ?? '' }}</div>
      </div>
      <div class="info d-flex" v-if="euroNorms && euroNorms.fuel_type">
        <div class="label">{{ t('fuel-type') }}:</div>
        <div class="fw-bold value">{{ euroNorms.fuel_type ?? '' }}</div>
      </div>
    </div>
    <!-- Szczegóły pojazdu i interaktywne akcje -->
    <div class="details">
      <div class="d-flex justify-content-between actions">
        <!-- Przycisk powrotu (do listy) -->
        <!-- <button class="btn-prev" :class="moment(vehicleData.checkpointTime).isAfter(moment()) ? '' : 'no-timer'" @click="back()">
          <span>{{ t('back').toUpperCase() }}</span>
        </button> -->
        <!-- Timer - czas do przyjazdu -->
        <!-- <div class="timer" v-if="moment(vehicleData.checkpointTime).isAfter(moment())">
          {{ t('arrival') }}:
          <CountdownTimer
            :deadline="
              moment(vehicleData.checkpointTime).format('YYYY/MM/DD HH:mm:ss')
            "
          />
        </div> -->
        <!-- przycisk "Back" – klasa zależna od checkpoint + momentDiffServer -->
        <!-- Powrót — dokładnie ten sam wzorzec co pozostałe przyciski powrotu
             w aplikacji (ArchivalMeasurements, BlackList, ContactList):
             `ds-btn ds-btn--secondary` + strzałka w `ds-btn__arrow`.
             Klasa `no-timer` została usunięta — nie ma już reguły, która by ją
             obsługiwała (pozycjonowała ikonę `:before` ze starego designu). -->
        <button
          type="button"
          class="ds-btn ds-btn--secondary btn-prev"
          @click="back()"
        >
          <span class="ds-btn__arrow" aria-hidden="true">&#8592;</span>
          {{ t('back').toUpperCase() }}
        </button>

        <!-- Timer – widoczny dopóki checkpoint + momentDiffServer > teraz -->
        <div class="timer"
            v-if="moment(vehicleData.checkpointTime).add(momentDiffServer, 'ms').isAfter(moment())">
          {{ t('arrival') }}:
          <CountdownTimer
            :deadline="moment(vehicleData.checkpointTime).format('YYYY/MM/DD HH:mm:ss')"
            :server-diff="momentDiffServer"
          />
        </div>
        <!-- Akcje dla scenariuszy VMS i pobierania PDF -->
        <div class="buttons">
          <!-- Akcje związane z VMS (wyświetlanie tablicy, itp.) -->
          <div v-if="vehicleData.plateVmsReadablePointAt" class="dropdown-wrapper" ref="dropdown">
            <button class="btn" :disabled="!canManageVms" @click="handleVmsButton()">
              <span style="padding: 10px;">{{ buttonText }}</span>
            </button>
            <ul v-show="isOpen" class="dropdown-list">
              <li @click="displayAllTrucks">{{ t('all-trucks') }}</li>
              <li @click="displayPlateNumber">{{ t('plate-no') }}</li>
            </ul>
            <!-- Informacja zwrotna o aktualnej treści znaku wysterowanej przez użytkownika/administratora (zad. 4) -->
            <div v-if="vmsDisplayedBy" class="vms-displayed-by">
              <span>{{ t('vms-displayed-by') }}: {{ vmsDisplayedBy }}</span>
              <span v-if="vmsOverride" class="vms-override-badge">{{ t('vms-admin-override') }}</span>
            </div>
          </div>
          <!-- Pobieranie raportu PDF -->
          <button class="btn btn-download" @click="retrieveZip">
            <span>PDF</span>
          </button>
        </div>
      </div>
      <!-- Zdjęcia pojazdu (przełączanie kliknięciem) -->
      <div class="row" id="images" v-if="vehicleData.images?.length > 0">
        <div @click="nextImage" style="cursor: pointer;">
          <vehicle-image
            :vehicle="vehicleData"
            :image-service="vehService"
            :set-image-with-number="currentImageIndex + 1"
            :image="vehicleData.images[currentImageIndex]"
            v-on:wasSet="wasSetCallback"
          />
        </div>
      </div>
      <!-- Parametry pojazdu (masy, długość, prędkość, itp.) -->
      <div class="vehicle-params" v-if="!shouldHideLocation">
        <h3>
          {{ t('vehicle-parameters') }}
        </h3>
        <div class="d-flex vehicle-data" id="vehicle-data">
          <!-- Lewa kolumna: masy i przeciążenia -->
          <div class="col-4">
            <div class="row" :class="{ violation: vehicleData.overloadSA }">
              <div class="col-9">{{ t('vehicle-axle') }} [%]</div>
              <div class="col-3">{{ evaluateInt(vehicleData.overloadSA) }}</div>
            </div>
            <div class="row" :class="{ violation: vehicleData.overloadMA }">
              <div class="col-9">{{ t('multiple-axises') }} [%]</div>
              <div class="col-3">{{ evaluateInt(vehicleData.overloadMA) }}</div>
            </div>
            <div class="row" :class="{ violation: vehicleData.overloadTL }">
              <div class="col-9">{{ t('dmc') }} [%]</div>
              <div class="col-3">{{ evaluateInt(vehicleData.overloadTL) }}</div>
            </div>
          </div>
          <!-- Środek: typy, długość, wysokość, prędkość, liczba osi -->
          <div class="col-4">
            <div class="row">
              <div class="col-9">{{ t('vehicle-type') }}</div>
              <div class="col-3">
                {{ vehicleData.ctg8p1 }} / {{ vehicleData.ctg323 }}
              </div>
            </div>
            <div class="row">
              <div class="col-9">{{ t('length') }} [cm]</div>
              <div class="col-3">{{ evaluateInt(vehicleData.vehLength) }}</div>
            </div>
            <div class="row" :class="{ violation: vehicleData.overheight }">
              <div class="col-9">{{ t('height') }} [cm]</div>
              <div class="col-3">
                {{ evaluateDecimal(vehicleData.vehHeight) }}
              </div>
            </div>
            <div class="row" :class="{ violation: vehicleData.overspeed }">
              <div class="col-9">{{ t('speed') }} [km/h]</div>
              <div :class="'col-3 measurement-row-field-b'">
                {{ evaluateInt(vehicleData.speed) }}
              </div>
            </div>
            <div class="row">
              <div class="col-9">{{ t('axles-no') }}</div>
              <div class="col-3">
                {{ evaluateInt(vehicleData.numberOfAxles) }}
              </div>
            </div>
          </div>
          <!-- Prawa kolumna: naruszenia i wyjazdy poza pas -->
          <div class="border-right-0 col-4">
            <div class="row" :class="{ violation: vehicleData.wrongDirection }">
              <div class="col-9">{{ t('wrong-direction') }}</div>
              <div class="col-3">
                {{ evaluateBoolean(vehicleData.wrongDirection) }}
              </div>
            </div>
            <div class="row" :class="{ violation: vehicleData.overlane }">
              <div class="col-9">{{ t('outside-the-lane') }}</div>
              <div class="col-3">
                {{ evaluateBoolean(vehicleData.overlane) }}
              </div>
            </div>
            <div class="row" :class="{ violation: vehicleData.overheight }">
              <div class="col-9">{{ t('height') }}</div>
              <div class="col-3">
                {{ evaluateBoolean(vehicleData.overheight) }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Masa całkowita z klasą dokładności -->
      <div class="axis-info" v-if="!shouldHideLocation">
        <h3>
          {{ t('total-weight-with-accuracy-class') }}<template v-if="accuracyClassName">: {{ accuracyClassName }}</template>
        </h3>
        <div class="row center-aligned">
          <div class="axis-info-data-mass">
            <!-- Tabela z minimalną, aktualną i maksymalną masą całkowitą -->
            <div class="row" id="axisTopRow">
              <div class="col">-</div>
              <div class="col">
                {{ vehicleData.axlesComplex?.minTotalLoad }}
              </div>
            </div>
            <div class="row" id="axisNameRow">
              <div class="col middle-axis-value">kg</div>
              <div class="col middle-axis-value" :class="{ overload: vehicleData.overload }">
                {{ vehicleData.axlesComplex?.totalLoad }}
              </div>
            </div>
            <div class="row" id="axisBottomRow">
              <div class="col">+</div>
              <div class="col">
                {{ vehicleData.axlesComplex?.maxTotalLoad }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Rozstaw osi i masy osi -->
      <div class="axis-info" v-if="!shouldHideLocation">
        <h3>
          {{ t('axle-spacing-and-load-with-accuracy-class') }}<template v-if="accuracyClassName">: {{ accuracyClassName }}</template>
        </h3>
        <table class="axis-table">
          <thead>
            <!-- Nagłówki dla rozstawów osi (góra, środek, dół) -->
            <tr class="row-top">
              <th>-</th>
              <th
                v-for="(group, i) in mergedAxlesData"
                :key="'minus-' + i"
                :colspan="group.colspan"
                :class="{ 'wheel-diff-gap': group.isGap }"
              >
                <template v-if="!group.isGap">
                  <span :class="{ 'overload': group.overload == true }">
                    {{ group.dataAxMinus }}
                  </span>
                </template>
              </th>
            </tr>

            <tr class="row-center">
              <td>{{ t('axle').toUpperCase() }}</td>
              <template v-for="(group, i) in mergedAxlesData" :key="'center-' + i">
                <td
                  :colspan="group.colspan"
                  :class="{ 'wheel-diff-gap': group.isGap }"
                >
                  <span v-if="!group.isGap" :class="{ 'overload': group.overload == true }">
                    {{ group.dataAxCenter }}
                  </span>
                </td>
              </template>
            </tr>
            <tr class="row-bottom">
              <th>+</th>
              <th v-for="(ax, i) in mergedAxlesData" :key="'plus-' + i" :colspan="ax.colspan">
                <span :class="{ 'overload': ax.overload == true }">
                  {{ ax.dataAxPlus }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr class="row-wheels-top">
              <th>{{ t('wheel').toUpperCase() }} [kg]<br />{{ t('right').toUpperCase() }}</th>
              <template v-for="(ax, i) in axlesData" :key="'top-' + i">
                <td>{{ ax.dataRight }}</td>
                <td v-if="ax.gapAfter > 0" class="wheel-diff-gap"></td>
              </template>
            </tr>

            <tr class="row-wheels-icons">
              <th>[cm]</th>
              <template v-for="(ax, i) in axlesData" :key="'diff-' + i">
                <td>
                  <div class="wheel-icon" :class="{ overload: ax.overload }">
                    <img
                      :src="require(`@/assets/icons/${ax.overload ? 'kola_02' : 'kola_01'}.png`)"
                      alt=""
                    />
                  </div>
                </td>
                <td v-if="ax.gapAfter > 0" class="wheel-diff-gap">
                  {{ ax.gapAfter }}
                </td>
              </template>
            </tr>

            <tr class="row-wheels-bottom">
              <th>{{ t('left').toUpperCase() }}</th>
              <template v-for="(ax, i) in axlesData" :key="'bottom-' + i">
                <td>{{ ax.dataLeft }}</td>
                <td v-if="ax.gapAfter > 0" class="wheel-diff-gap"></td>
              </template>
            </tr>
          </tbody>
        </table>
<!--        <div class="d-flex justify-content-between actions-down">-->
<!--          <button class="btn-prev" @click="back()">-->
<!--            <span>{{ t('back').toUpperCase() }}</span>-->
<!--          </button>-->
<!--        </div>-->
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import CurrentMeasurementsService from "@/services/CurrentMeasurementsService";
import ArchivalMeasurementsService from "@/services/ArchivalMeasurementsService";
import FiltersService from "@/services/FiltersService";
import CountryFlag from "vue-country-flag-next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import VehicleImage from "@/components/VehicleImage";
import CountdownTimer from "@/components/current/countdown-timer";
// import { useCurrentFiltersStore } from "@/stores/currentFilters";
import { useUserStore } from "@/stores/user.store";
import {useCurrentStore} from "@/stores/current";
import { isSG } from "@/config/appVersion";
import { useArchivalStore } from "@/stores/archival";
import { useArchivalFiltersStore } from "@/stores/archivalFilters";


moment.locale("pl");
const getCountryISO2 = require("country-iso-3-to-2");
const defaultLeafletMarkerIcon = L.icon({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

if (!L.__safeOverlayPatchedForVehicleDetails) {
  const originalTooltipUpdate = L.Tooltip.prototype._updatePosition;
  L.Tooltip.prototype._updatePosition = function (...args) {
    if (!this._map) return;
    return originalTooltipUpdate.apply(this, args);
  };

  const originalPopupUpdate = L.Popup.prototype._updatePosition;
  L.Popup.prototype._updatePosition = function (...args) {
    if (!this._map) return;
    return originalPopupUpdate.apply(this, args);
  };

  const originalTooltipAnimateZoom = L.Tooltip.prototype._animateZoom;
  L.Tooltip.prototype._animateZoom = function (...args) {
    if (!this._map) return;
    return originalTooltipAnimateZoom.apply(this, args);
  };

  const originalPopupAnimateZoom = L.Popup.prototype._animateZoom;
  L.Popup.prototype._animateZoom = function (...args) {
    if (!this._map) return;
    return originalPopupAnimateZoom.apply(this, args);
  };

  L.__safeOverlayPatchedForVehicleDetails = true;
}

// Rejestracja komponentów Chart.js potrzebnych do wykresów (jeśli występują)
ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
);

export default {
  name: "VehicleDetails",
  // Po zamontowaniu komponentu - ustaw krok jeśli jest w URL, nasłuchuj na kliknięcie poza dropdownem
  async mounted() {
    const step = this.$route.query.step;
    if (step) {
      this.previousStep = parseInt(step);
    }

    document.addEventListener('click', this.onClickOutside);

    if (this.userStore && typeof this.userStore.self === 'function') {
      await this.userStore.self();
    }
    const prefs  = (this.userStore && this.userStore.preferences) || {};
    const addSec = Number(prefs.checkpointAdditionalTime || 0);
    this.momentDiffServer = addSec * 1000; // ms

    if (!this.isSG) {
      await this.loadLocationsLookup();
      this.$nextTick(() => {
        this.renderMiniMapMarker();
      });
    }
  },
  // Usuń nasłuchiwanie na kliknięcie po zdemontowaniu komponentu
  beforeUnmount() {
    document.removeEventListener('click', this.onClickOutside);

    // Bez tego pętla odpytywania o status znaku VMS żyła dalej po wyjściu ze szczegółów pojazdu
    // i co sekundę wysyłała żądanie — przy kilku odwiedzonych pojazdach zauważalnie obciążało aplikację.
    if (this.plateVmsStatusTimeout) {
      clearTimeout(this.plateVmsStatusTimeout);
      this.plateVmsStatusTimeout = null;
    }

    if (this.miniMap) {
      if (this.miniMapMarker) {
        this.miniMapMarker.unbindTooltip();
        this.miniMap.removeLayer(this.miniMapMarker);
      }
      this.miniMap.remove();
      this.miniMap = null;
      this.miniMapMarker = null;
    }
  },
  components: {
    CountryFlag,
    VehicleImage,
    CountdownTimer,
  },
  props: {
    timerVisible: {
      default: true,
    },
  },

  data() {
    return {
      moment: moment,
      // Obiekt z danymi pojazdu. Wypełniany najpierw danymi z listy pomiarów (natychmiastowy render),
      // a następnie odświeżany odpowiedzią z API.
      vehicle: null,
      // Dane o osiach pojazdu
      axlesData: [],
      // Dane do wykresów
      chartData: null,
      chartOptions: {
        responsive: true,
      },
      selectedSmallImage: 0,
      isDownloading: false,
      currentImageIndex: 0,
      previousStep: null,
      previousQuery: {},
      isOpen: false, // Czy rozwinięta jest lista opcji VMS
      canManageVms: false, // Czy użytkownik może zarządzać znakiem VMS
      vmsContentDisplayed: false, // Czy na znaku jest aktualnie wyświetlana treść (do obsługi przycisku)
      vmsDisplayedBy: null, // Kto wysterował aktualną treść (informacja zwrotna, zad. 4)
      vmsOverride: false, // Czy bieżący użytkownik przejmuje treść jako administrator nadrzędny
      buttonText: 'Scenariusz dla znaku nie jest włączony',
      momentDiffServer: 0, // Korekta czasu względem serwera
      userStore: useUserStore(),
      euroNorms: null,
      // Czy trwa dociąganie norm EURO z zewnętrznej usługi (steruje spinnerem w panelu danych pojazdu).
      euroNormsLoading: false,
      isSG,
      miniMap: null,
      miniMapMarker: null,
      locationsLookup: [],
      // Uchwyt cyklicznego odpytywania o status znaku VMS — musi być anulowany po opuszczeniu widoku.
      plateVmsStatusTimeout: null,
    };
  },
  computed: {
    // Wyciąganie wartości z .env./// żeby nie wyświetlać różnych elementów strony dla różnych klientów
    shouldHideLocation() {
      return isSG;
    },
    // Zwraca dane pojazdu do szablonu
    vehicleData() {
      return this.vehicle;
    },
    // Nazwa klasy dokładności pasa ruchu. Lista pomiarów nie serializuje `lane.accuracyClass`
    // (jest tylko w grupie szczegółów), więc przy wstępnym renderze z listy pola jeszcze nie ma —
    // pojawi się po odświeżeniu danych odpowiedzią z API.
    accuracyClassName() {
      return this.vehicleData?.lane?.accuracyClass?.name || "";
    },
    // Wybiera odpowiedni serwis w zależności od widoku (aktualny/archiwalny)
    vehService() {
      if (this.$route.path.startsWith("/vehicleArch")) {
        return ArchivalMeasurementsService;
      }
      return CurrentMeasurementsService;
    },
    // Czy można pobrać raport PDF (tylko w archiwalnych)
    zipAvailable() {
      if (this.$route.path.startsWith("/vehicleArch")) {
        return true;
      }
      return false;
    },
    selectedSmall() {
      return this.selectedSmallImage;
    },
    miniMapPoint() {
      const vehicleLocation = this.vehicleData?.location || {};
      const locationId = Number(vehicleLocation.id);
      const lookupLocation = this.locationsLookup.find(
        (loc) => Number(loc?.id) === locationId
      );
      const source = lookupLocation || vehicleLocation;

      const lat = parseFloat(source?.geoLat ?? source?.geo_lat);
      const lng = parseFloat(source?.geoLong ?? source?.geo_long);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
      }

      return {
        lat,
        lng,
        label: source?.shortName || source?.name || `ID ${locationId}`,
      };
    },
    hasMiniMapPoint() {
      return !!this.miniMapPoint;
    },
    // Łączy dane osi do prezentacji w tabeli (grupowanie osi z małymi odstępami)
    mergedAxlesData() {
      const merged = [];
      const data = this.axlesData;
      let i = 0;

      while (i < data.length) {
        const group = [data[i]];
        let j = i;

        // Grupuje kolejne osie z niewielkimi przerwami
        while (
          j + 1 < data.length &&
          data[j].gapAfter > 0 &&
          data[j].gapAfter <= 180
        ) {
          j++;
          group.push(data[j]);
        }

        const groupLength = group.length;
        const colspan = 2 * groupLength - 1;

        // Tworzy obiekt grupy osi
        const mergedGroup = {
          colspan,
          groupSize: groupLength,
          overload: group.some(ax => ax.overload),
          axes: group,
          dataAxMinus: group.reduce((sum, g) => sum + (g.dataAxMinus || 0), 0),
          dataAxCenter: group.reduce((sum, g) => sum + (g.dataAxCenter || 0), 0),
          dataAxPlus: group.reduce((sum, g) => sum + (g.dataAxPlus || 0), 0),
          dataLeft: group.reduce((sum, g) => sum + (g.dataLeft || 0), 0),
          dataRight: group.reduce((sum, g) => sum + (g.dataRight || 0), 0),
        };

        merged.push(mergedGroup);

        // Dodaje specjalny obiekt jeśli odstęp między osiami jest duży (np. naczepa)
        const lastAx = group[group.length - 1];
        if (lastAx.gapAfter > 180) {
          merged.push({
            isGap: true,
            colspan: 1,
            gapAfter: lastAx.gapAfter,
          });
        }

        i += groupLength;
      }

      return merged;
    },
  },
  // Po utworzeniu komponentu: reaguj na zmianę id pojazdu w trasie i pobieraj nowe dane
  created() {
    this.$watch(
      () => this.$route.params.id,
      () => {
        // Najpierw pokazujemy to, co już mamy z listy pomiarów — widok pojawia się od razu,
        // bez czekania na odpowiedź API. Odpowiedź tylko odświeży te same dane.
        this.hydrateFromList();
        this.fetchData();
      },
      { immediate: true }
    );
  },
  watch: {
    miniMapPoint() {
      if (this.isSG) return;
      this.$nextTick(() => {
        this.renderMiniMapMarker();
      });
    },
  },
  methods: {
    // Szuka danych pojazdu na liście pomiarów, z której użytkownik wszedł w szczegóły.
    // Lista serializuje ten sam zestaw pól co szczegóły, więc widok da się wyrenderować od razu.
    hydrateFromList() {
      // Nie nadpisujemy danych, jeżeli dotyczą już tego pojazdu (np. przy odświeżeniu).
      const routeId = String(this.$route.params.id);
      if (this.vehicle && String(this.vehicle.id) === routeId) {
        return;
      }

      const isArch = this.$route.path.startsWith("/vehicleArch");
      const list = isArch
        ? useArchivalFiltersStore().savedVehicles
        : useCurrentStore().vehicles;

      if (!Array.isArray(list) || list.length === 0) {
        return;
      }

      const locationId = String(this.$route.params.location);
      const found = list.find(
        (veh) =>
          String(veh?.id) === routeId &&
          String(veh?.location?.id) === locationId
      );

      if (found) {
        // Kopia, bo setVehicleData podmienia pola (checkpointTime, timeToRemove) — obiekt w store
        // musi zostać nietknięty.
        this.setVehicleData({ ...found });
      }
    },
    // Dociąga normy EURO z zewnętrznej usługi. Świadomie osobne żądanie — czas odpowiedzi tej usługi
    // jest poza naszą kontrolą i nie może opóźniać wyświetlenia szczegółów pojazdu.
    loadEuroNorms(vehicle) {
      // Endpoint istnieje tylko dla pomiarów bieżących, a usługa i tak obsługuje wyłącznie pojazdy krajowe.
      if (this.$route.path.startsWith("/vehicleArch") || vehicle?.nationality !== "POL") {
        this.euroNormsLoading = false;
        return;
      }

      this.euroNormsLoading = true;

      // Zwracamy łańcuch, żeby wywołujący (i testy) mogli poczekać na zakończenie dociągania.
      return CurrentMeasurementsService.getEuroNorms(vehicle.id, vehicle.location?.id)
        .then((data) => {
          // Zabezpieczenie przed wyścigiem: w międzyczasie użytkownik mógł przejść do innego pojazdu.
          if (String(this.vehicle?.id) === String(vehicle.id)) {
            this.euroNorms = data?.euroNorms ?? null;
          }
        })
        .catch(() => {
          this.euroNorms = null;
        })
        .finally(() => {
          // Spinner gasimy tylko wtedy, gdy to wciąż ten sam pojazd — dla nowego trwa już nowe żądanie.
          if (String(this.vehicle?.id) === String(vehicle.id)) {
            this.euroNormsLoading = false;
          }
        });
    },
    async loadLocationsLookup() {
      if (this.isSG) {
        return;
      }
      if (this.locationsLookup.length) {
        return;
      }

      try {
        const locations = await FiltersService.getLocations();
        this.locationsLookup = Array.isArray(locations) ? locations : [];
      } catch (error) {
        console.warn("Nie udało się pobrać lokalizacji dla minimapy:", error);
      }
    },
    ensureMiniMap() {
      if (this.isSG) {
        return;
      }
      if (this.miniMap || !this.$refs.miniMapContainer) {
        return;
      }

      this.miniMap = L.map(this.$refs.miniMapContainer, {
        center: [52.0, 19.0],
        zoom: 6,
        minZoom: 5,
        maxZoom: 18,
        zoomAnimation: false,
        markerZoomAnimation: false,
        fadeAnimation: false,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 18,
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(this.miniMap);
    },
    renderMiniMapMarker() {
      if (this.isSG) {
        return;
      }
      this.ensureMiniMap();
      if (!this.miniMap) {
        return;
      }

      const point = this.miniMapPoint;
      if (!point) {
        if (this.miniMapMarker) {
          this.miniMap.removeLayer(this.miniMapMarker);
          this.miniMapMarker = null;
        }
        this.miniMap.setView([52.0, 19.0], 6);
        this.miniMap.invalidateSize();
        return;
      }

      const latLng = [point.lat, point.lng];
      if (!this.miniMapMarker) {
        this.miniMapMarker = L.marker(latLng, {
          icon: defaultLeafletMarkerIcon,
        }).addTo(this.miniMap);
      } else {
        this.miniMapMarker.setLatLng(latLng);
      }

      this.miniMapMarker.bindTooltip(point.label, {
        direction: "top",
        offset: [0, -8],
        opacity: 0.9,
      });

      this.miniMap.setView(latLng, 13);
      this.miniMap.invalidateSize();
    },
    // Obsługa kliknięcia przycisku VMS - pokazuje dropdown lub czyści treść na znaku
    handleVmsButton() {
      if (this.vmsContentDisplayed) {
        // jeśli aktualnie jest wyświetlana treść (własna lub przejmowana administracyjnie) - anuluj/wygaś
        this.clearVms();
      } else if (this.canManageVms) {
        // w przeciwnym razie rozwiń/zwiń dropdown
        this.isOpen = !this.isOpen;
      }
    },
    // Wyślij polecenie do znaku VMS, aby wyświetlił komunikat dla wszystkich ciężarówek
    displayAllTrucks() {
      CurrentMeasurementsService.displayPlateOrAllTrucks(this.vehicle.id, true).then(() => {
        this.isOpen = false;
      });
    },
    // Wyślij polecenie do znaku VMS, aby wyświetlił numer rejestracyjny pojazdu
    displayPlateNumber() {
      CurrentMeasurementsService.displayPlateOrAllTrucks(this.vehicle.id, false).then(() => {
        this.isOpen = false;
      });
    },
    // Wyślij polecenie do znaku VMS, aby usunął treść z tablicy
    clearVms() {
      CurrentMeasurementsService.clearPlateVms(this.vehicle.id).then(() => {
        this.canManageVms = false;
        this.isOpen = false;
        this.vmsContentDisplayed = false;
        this.vmsDisplayedBy = null;
        this.vmsOverride = false;
      });
    },
    // Zamknij dropdown po kliknięciu poza nim
    onClickOutside(e) {
      const dropdown = this.$refs.dropdown;

      if (dropdown && !dropdown.contains(e.target)) {
        this.isOpen = false;
      }
    },
    // Buduje obiekt osi do tabeli (pojedyncza oś)
    _buildSingleAxle(ax) {
      return {
        colspan: 1,
        overload: ax.overload,
        dataAxMinus: ax.dataAxMinus,
        dataAxCenter: ax.dataAxCenter,
        dataAxPlus: ax.dataAxPlus,
        dataLeft: ax.dataLeft,
        dataRight: ax.dataRight,
      };
    },
    // Pobiera dane pojazdu z backendu
    fetchData() {
      this.error = this.post = null;

      // Dane z usługi EURO dotyczą konkretnego pomiaru — przy przejściu na inny czyścimy poprzednie.
      // Spinner włączamy od razu (nie po odpowiedzi z API), żeby użytkownik od wejścia widział,
      // że w tym miejscu dopiero pojawią się dane. Dla pomiarów archiwalnych i pojazdów
      // zagranicznych norm nie pobieramy, więc spinnera nie pokazujemy.
      this.euroNorms = null;
      this.euroNormsLoading =
        !this.$route.path.startsWith("/vehicleArch") &&
        (!this.vehicle?.nationality || this.vehicle.nationality === "POL");

      // Przy przejściu na innego pojazdu komponent jest ponownie użyty — kasujemy odpytywanie
      // o status znaku VMS dla poprzedniego pomiaru, żeby nie działały dwie pętle naraz.
      if (this.plateVmsStatusTimeout) {
        clearTimeout(this.plateVmsStatusTimeout);
        this.plateVmsStatusTimeout = null;
      }

      this.vehService
        .get(
          this.$route.params.id,
          this.$route.params.location,
          this.$route.params.createdAt
        )
        .then((data) => {
          // const payload = data && data.vehicle
          //   ? data
          //   : { vehicle: data, euroNorms: null, euroMeta: null };
          //
          // const { vehicle, euroNorms } = payload;
          //
          // this.setVehicleData(vehicle);
          // this.euroNorms = euroNorms;   // <- zapamiętaj normy EURO do wyświetlenia
          // // this.euroMeta  = euroMeta;
          //
          // // Sprawdź czy znak VMS dostępny dla tego pojazdu
          // if (data.vehicle.plateVmsReadablePointAt) {
          //   this.isVmsAvailable(data.vehicle.id);
          // }
          const vehicleObj = data?.vehicle ?? data;

          this.setVehicleData(vehicleObj);

          // Normy EURO pochodzą z zewnętrznej usługi — dociągamy je po wyświetleniu szczegółów.
          this.loadEuroNorms(vehicleObj);

          if (!this.isSG) {
            this.loadLocationsLookup().finally(() => {
              this.$nextTick(() => {
                this.renderMiniMapMarker();
              });
            });
          }

          // Sprawdź VMS na ujednoliconym obiekcie
          if (vehicleObj?.plateVmsReadablePointAt) {
            this.isVmsAvailable(vehicleObj.id);
          }
        })
        .catch(() => {
          // Bez danych pojazdu nie ma po co dociągać norm EURO — spinner nie może kręcić się bez końca.
          this.euroNormsLoading = false;
        });
    },
    // Ustawia dane pojazdu w stanie komponentu
    setVehicleData: function (data) {
      if (data) {
        this.vehicle = data;
        this.vehicle.checkpointTime = new Date(this.vehicle.checkpointTime);
        this.vehicle.timeToRemove = new Date(this.vehicle.checkpointTime.getTime() - this.momentDiffServer);
        this.chartData = this.getChartData(this.vehicle);
        this.axlesData = this.getAxlesData(this.vehicle.axles);
        if (!this.isSG) {
          this.$nextTick(() => {
            this.renderMiniMapMarker();
          });
        }
      }
    },
    // Sprawdź dostępność i status VMS dla pojazdu (pojedynczy polling + automatyczny polling co sekundę)
    isVmsAvailable(id) {
      this.vehService.getPlateVmsStatus(id).then((data) => {
        this.displayPlateVmsStatus(id, data);
      })
      .catch(() => {
        this.canManageVms = false;
        this.isOpen = false;
      })
      .finally(() => {
        this.startGetPlateVmsStatus(id);
      });
    },
    // Aktualizuje tekst i stan przycisku na podstawie statusu VMS
    displayPlateVmsStatus(id, plateVmsStatus) {
      const plateVms = plateVmsStatus.plateVms;

      // Informacja zwrotna (zad. 4): kto wysterował aktualną treść.
      this.vmsContentDisplayed = !!plateVms.contentDisplayed;
      this.vmsDisplayedBy = plateVms.contentDisplayed ? plateVmsStatus.displayedBy : null;
      this.vmsOverride = false;

      // Etykieta aktualnie wyświetlanej treści (tabliczka lub numer rejestracyjny).
      const contentLabel = plateVms.allTrucksDisplayed
        ? 'Pojazdy ciężarowe'
        : (plateVmsStatus.displayedContent || this.vehicle.plateLpr);

      if (plateVmsStatus.manageable) {
        if (plateVms.contentDisplayed) {
          if (plateVmsStatus.ownedByCurrentUser) {
            // Własna treść — użytkownik może ją anulować.
            this.buttonText = 'Anuluj wyświetlanie treści: ' + contentLabel;
          } else {
            // Cudza treść, którą bieżący użytkownik może przejąć jako administrator nadrzędny (zad. 2).
            this.vmsOverride = true;
            this.buttonText = 'Anuluj treść administracyjnie: ' + contentLabel;
          }
        } else {
          const timeToVms = this.createCheckpointTime(this.vehicle.plateVmsReadablePointAt);
          this.buttonText = 'Wyświetl wybraną treść na znaku - ' + timeToVms;
        }
        this.canManageVms = true;
      } else {
        // Obsługa różnych przypadków braku dostępności znaku/scenariusza
        if (!plateVms.available) {
          this.buttonText = 'Znak niedostępny';
        } else if (!plateVms.scenarioEnabled) {
          this.buttonText = 'Scenariusz dla znaku nie jest włączony';
        } else if (!plateVms.contentDisplayed) {
          this.buttonText = 'Pojazd minął już znak';
        } else if (plateVms.vehicle.id == id) {
          this.buttonText = 'Wytypowano już ten pojazd do kontroli';
        } else {
          this.buttonText = 'Wytypowano już inny pojazd do kontroli';
        }
        this.canManageVms = false;
      }
    },
    // Oblicz różnicę czasu do wyświetlenia treści na znaku VMS
    createCheckpointTime(plateVmsReadablePointAt) {
      const serverTime = new Date(Date.now() + this.momentDiffServer);
      const plateMs    = Date.parse(plateVmsReadablePointAt);
      const serverMs   = serverTime.getTime();

      const timeDiffMs = Math.round((plateMs - serverMs) / 1000) * 1000;
      const diffDate   = new Date(Math.abs(timeDiffMs));

      const hh = diffDate.getUTCHours().toString().padStart(2, '0');
      const mm = diffDate.getUTCMinutes().toString().padStart(2, '0');
      const ss = diffDate.getUTCSeconds().toString().padStart(2, '0');

      const timeString = [
        ...(hh !== '00' ? [hh] : []),
        mm,
        ss
      ].join(':');

      return timeDiffMs >= 0 ? timeString : '-' + timeString;
    },
    // Polling statusu VMS co sekundę (dopóki można zarządzać tablicą)
    startGetPlateVmsStatus(id) {
      let nowInMiliseconds = (new Date()).getTime() + 3000;
      const checkpointAdditionalTime = this.momentDiffServer * 1000;
      nowInMiliseconds -= checkpointAdditionalTime;

      if ((this.vehicle.timeToRemove.getTime() - nowInMiliseconds) >= 0) {
        this.plateVmsStatusTimeout = setTimeout(() => {
          this.plateVmsStatusTimeout = null;
          this.isVmsAvailable(id);
        }, 1000);
      }
    },
    // Formatuje boolean jako TAK/NIE (dla prezentacji w tabeli)
    evaluateBoolean(bool) {
      if (bool) {
        return "TAK";
      }
      return "NIE";
    },
    // Formatuje liczby całkowite do prezentacji
    evaluateInt(value) {
      if (value) {
        return value;
      }
      return 0;
    },
    // Formatuje liczby zmiennoprzecinkowe do prezentacji
    evaluateDecimal(value) {
      if (value) {
        return value;
      }
      return "0,00";
    },
    // Pobiera kod ISO-2 kraju na podstawie ISO-3 (do flag)
    getCode(value) {
      return getCountryISO2(value);
    },
    // Tworzy dane do wykresu na podstawie próbek kształtu pojazdu (shapeSamples)
    getChartData(vehicleData) {
      if (!vehicleData) {
        return {
          labels: [" "],
          datasets: [{ data: [] }],
        };
      }
      let dataset = [];
      let labelsData = [];
      if (vehicleData.shapeSamples) {
        for (const dataInt in vehicleData.shapeSamples) {
          dataset.push(vehicleData.shapeSamples[dataInt]);
          labelsData.push("");
        }
      }

      return {
        labels: labelsData,
        datasets: [{ data: dataset }],
      };
    },
    // Tworzy dane o osiach pojazdu (do tabeli z masami i naciskami)
    getAxlesData(axles) {
      const list = axles ? Array.from(axles) : [];

      return list.map((exData, index) => ({
        dataAxMinus: exData.minTotalLoad,
        dataAxCenter: exData.totalLoad,
        dataAxPlus: exData.maxTotalLoad,
        dataLeft: exData.leftWeight,
        dataRight: exData.rightWeight,
        // API podaje `gap` jako odległość do osi poprzedzającej, a tabela rysuje odstęp
        // za osią — bierzemy go więc z osi następnej. Za ostatnią osią odstępu nie ma.
        gapAfter: Number(list[index + 1]?.gap) || 0,
        overload: exData.overload,
      }));
    },
    // Czy użytkownik ma historię powrotu (do przycisku "back")
    hasHistory() {
      return window.history.length > 1;
    },
    // Zwraca klasę CSS dla przeciążonej masy całkowitej
    getWeightClassName(vehicle) {
      if (vehicle.totalLoad >= vehicle.limitTotalLoad) {
        return "measurement-row-field-b-red";
      }
      return "measurement-row-field-b";
    },
    // Zwraca klasę CSS dla przekroczenia prędkości
    getSpeedClassName(vehicle) {
      if (vehicle.overspeed) {
        return "measurement-row-field-b-red";
      }
      return "measurement-row-field-b";
    },
    // Callback po zmianie miniatury zdjęcia
    wasSetCallback() {
      this.selectedSmallImage = 0;
    },
    // Pobiera raport PDF (ZIP) z backendu
    retrieveZip() {
      this.isDownloading = true;
      this.vehService
        .getVehicleZip(this.vehicleData.id, "pdf-with-image")
        .then((data) => {
          this.vehService
            .fetch(data.link)
            .then((response) => {
              const link = document.createElement("a");
              link.href = URL.createObjectURL(response);

              // Generuje nazwę pliku na podstawie daty
              const now = new Date();
              const year = now.getFullYear();
              const month = String(now.getMonth() + 1).padStart(2, '0');
              const day = String(now.getDate()).padStart(2, '0');
              const hours = String(now.getHours()).padStart(2, '0');
              const minutes = String(now.getMinutes()).padStart(2, '0');
              const seconds = String(now.getSeconds()).padStart(2, '0');
              const fileName = `vehicle_report_${day}_${month}_${year}_${hours}_${minutes}_${seconds}`;
              link.download = fileName;

              link.click();
              this.isDownloading = false;
              URL.revokeObjectURL(link.href);
            })
            .catch(console.error);
        })
        .catch((error) => {
          this.loading = false;
          console.error(error);
        });
    },
    // Powrót do listy (lub widoku archiwalnego) i ewentualne wymuszenie przeładowania
    back() {
      const backTo =
        typeof this.$route.query.backTo === "string" &&
        this.$route.query.backTo.startsWith("/")
          ? this.$route.query.backTo
          : null;

      if (this.$route.path.startsWith("/vehicleArch")) {
        const page = this.$route.query.page;
        const limit = this.$route.query.limit;

        const archival = useArchivalStore();

        archival.$patch({
          step: 4,
          isResult: true,
        });

        if (backTo) {
          this.$router.push(backTo);
          return;
        }

        this.$router.push({
          path: "/archival",
          query: {
            ...(page ? { page: String(page) } : {}),
            ...(limit ? { limit: String(limit) } : {}),
          },
        });
        return;
      }

      const current = useCurrentStore();
      current.setStepValue(3);

      if (backTo) {
        this.$router.push(backTo);
        return;
      }

      this.$router.push({ path: "/" });
    },
    // Przełączanie zdjęcia pojazdu (po kliknięciu)
    nextImage() {
      const totalImages = this.vehicleData.images?.length || 0;
      if (totalImages === 0) {
        return;
      }
      this.currentImageIndex = (this.currentImageIndex + 1) % totalImages;
    },
    // Ustawienie aktualnego zdjęcia po indeksie
    setImage(number) {
      this.currentImageIndex = number - 1;
    },
  },
};
</script>

<style scoped>

.actions {
  background-color: var(--surface-primary);
  /* Przycisk powrotu, timer i PDF mają różne wysokości — bez tego siedziały
     na trzech różnych osiach (domyślne `align-items: stretch`). */
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xs) var(--spacing-md);
}

.actions-down {
  background-color: var(--surface-primary);
  min-height: 43px;
}

.download {
  margin: 8px;
  padding: 0;
}

.actions .pdf {
  margin-top: 5px;
}

.actions .btn-next, .actions-down .btn-next {
  margin-right: 50px;
  padding: 0 40px 8px 8px;
  position: relative;
  color: var(--text-secondary);
}

.actions .btn-next:hover, .actions-down .btn-next:hover {
  color: var(--text-primary);
}

/* Wygląd przycisku POWRÓT bierzemy w całości z globalnego `button-system.css`
   (36 px wysokości, wyśrodkowana treść, symetryczny padding). Wcześniejsze
   nadpisania były reliktem starego designu z ikoną strzałki w `:before`
   (`padding: 6px 0 0 40px` + `.no-timer:before`): ikony już nie ma, a rezerwacja
   40 px z lewej i 6 px z góry rozjeżdżała napis w pudełku i rozciągała pasek
   akcji do 47 px, obok 36-pikselowego przycisku PDF. */

.details-row-field {
  font-size: var(--font-size-md);
}

.details-row-field-b {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-md);
}

.vehicle-data {
  margin: 15px 0;
}

.vehicle-data > div {
  padding: 10px 50px;
  border-right: 1px solid var(--border-default);
}

.vehicle-data > div.border-right-0 {
  border-right: 0;
}

.vehicle-data > div .row {
  padding: 5px;
  border-bottom: 1px solid var(--border-default);
}

.vehicle-data > div .row .col-9 {
  text-align: left;
}

.vehicle-data > div .row .col-3 {
  text-align: center;
}

.side-info {
  /* Jedno wcięcie poziome dla wszystkich sekcji panelu (mapa, data, lokalizacja,
     wiersze etykieta/wartość) — wcześniej rozjeżdżały się 20px vs 15px. */
  --side-info-pad: 20px;
  /* Sztywna baza — bez `flex: 0 0` flex ściskał panel (sąsiad `.details` miał
     `width: 100%`), więc jego krawędź nie trafiała na wspólną oś lewej kolumny
     (logo w headerze, gutter nawigacji, sidebar filtrów = --left-column-width). */
  flex: 0 0 var(--sidebar-width);
  width: var(--sidebar-width);
  background: var(--surface-secondary);
  border-right: 1px solid var(--border-default);
}

.details {
  /* Baza 0 + shrink: kolumna bierze DOKŁADNIE pozostałą szerokość, nie liczy się
     z max-content zawartości (zdjęcie, tabele osi). */
  flex: 1 1 0%;
  min-width: 0;
}

/* Nic w kolumnie treści nie może być szersze od niej. Flex item ma domyślnie
   `min-width: auto`, więc kafelek ze zdjęciem nie kurczył się poniżej intrinsic
   width obrazka; a ponieważ `.row` niżej ma `justify-content: center`, nadmiar
   wypychany był SYMETRYCZNIE — czyli też w lewo, na panel boczny. */
.details .row,
.details .row > * {
  min-width: 0;
  max-width: 100%;
}

.details img,
.details table,
.details canvas {
  max-width: 100%;
}

.side-info .date-time,
.side-info .info {
  margin: 15px 0;
  font-size: var(--font-size-md);
  padding: 0 var(--side-info-pad);
  border-bottom: 1px solid var(--border-default);
}

.side-info .info {
  padding: 5px var(--side-info-pad) 18px;
}

.side-info .location {
  margin: 15px 0 0;
  font-size: var(--font-size-md);
  padding: 0 var(--side-info-pad);
}

.mini-map-section {
  margin: 12px 0 0;
  padding: 0 var(--side-info-pad) 18px;
  border-bottom: 1px solid var(--border-default);
  border-top: 1px solid var(--border-default);
}

.mini-map-section > span {
  display: block;
  margin: 12px 0 8px;
  font-size: var(--font-size-md);
}

.mini-map-wrapper {
  border: 1px solid var(--border-default);
  background: var(--surface-primary);
}

.mini-map-container {
  width: 100%;
  height: 220px;
}

.mini-map-no-data {
  margin: 0;
  padding: 10px;
  font-size: var(--font-size-sm);
  text-align: center;
  color: var(--text-muted);
}

.date-time > div,
.location > div {
  font-size: 24px;
  margin: 8px 0;
}

/* Data | godzina — jeden wiersz mieszczący się w panelu, bez ucinania sekund. */
.date-time .d-flex {
  align-items: baseline;
  min-width: 0;
}

.date-time .time {
  padding-left: 10px;
}

.date-time .date {
  padding-right: 10px;
  border-right: 1px solid var(--border-default);
}

/* .info .label {
  width: 160px;
  border-right: 1px solid var(--border-default);
}

.info .value {
  padding-left: 15px;
  font-size: var(--font-size-xl);
} */

/* wiersz z etykietą i wartością */
.info.d-flex {
  align-items: flex-start;       /* opcjonalnie: top-align przy 2+ liniach */
}

/* stała szerokość etykiety */
.info .label {
  border-right: 1px solid var(--border-default);
  flex: 0 0 160px;               /* równo 160px, bez kurczenia */
}

/* wartość: niech się łamie i zajmuje resztę */
.info .value {
  flex: 1 1 auto;
  min-width: 0;                  /* KLUCZOWE przy flex, inaczej tekst nie łamie */
  white-space: normal;           /* na wszelki wypadek */
  overflow-wrap: anywhere;       /* łamie też długie ciągi bez spacji */
  word-break: break-word;        /* fallback dla starszych przeglądarek */
  padding-left: 10px;
}

/* Spinner w miejscu danych z usługi EURO — czekamy na odpowiedź zewnętrznej usługi */
.info .value.euro-norms-loading {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm, 8px);
  font-weight: var(--font-weight-normal, 400);
  color: var(--text-secondary, inherit);
}

.euro-norms-loading__text {
  font-size: var(--font-size-sm);
}

h3 {
  margin-bottom: 0;
  font-weight: var(--font-weight-bold);
  padding: var(--spacing-md) var(--spacing-lg);
  background-color: var(--surface-primary);
}

.vehicle-params h3 {
  position: relative;
}

.vehicle-params h3 button {
  position: absolute;
  right: 30px;
  top: 10px;
  padding: 5px 15px 5px 25px;
}

.axis-info-data {
  font-size: var(--font-size-sm);
  padding: 25px;
  border-bottom: 1px solid var(--border-default);
}

.axis-info-data-table {
  font-size: var(--font-size-sm);
  margin: 25px;
  border-bottom: 1px solid var(--border-default);
  width: 100%;
}

.axis-info-data-table th:first-child {
  width: 135px;
}

.axis-info-data-table th:nth-child(2) {
  width: 50px;
  padding-right: 170px;
}

.axis-info-data-table th:nth-child(3) {
  width: 5px;
}

.axis-info-data-mass {
  width: 50%;
  justify-content: center;
  margin: 1rem;
}

.axis-wheels {
  font-size: var(--font-size-xl);
  padding: 25px;
}

.row {
  margin: 0;
  justify-content: center;
}

.row > * {
  padding: 0;
}

.plate-flag {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  background: var(--accent-blue);
  color: var(--text-inverse);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-sm);
}

/* Tablica rejestracyjna — główna informacja w szczegółach pomiaru. */
.plate {
  color: var(--text-primary);
  font-weight: var(--font-weight-bold);
  font-size: 28px;
  padding-left: var(--spacing-sm);
}

.plate-item {
  align-items: center;
  min-height: var(--control-height);
  background: var(--surface-primary);
  border-bottom: 1px solid var(--border-default);
}

.vl {
  border-left: 1px solid var(--border-default);
  height: 25px;
  top: 0;
}

.no-margin-top {
  margin-top: 0px;
}

.wheel-diff-val {
  width: 100%;
}

.wheel-diff-gap {
  font-weight: var(--font-weight-bold);
}

.measurement-row-field-b {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-md);
}

.measurement-row-field-b-red {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-md);
  color: var(--status-error);
}

.violation {
  background-color: var(--status-error);
  color: black !important; 
}

.axis-table {
  width: 100%;
  border-collapse: collapse;
  text-align: center;
  table-layout: fixed;
  background: var(--border-subtle);
}

.axis-table th,
.axis-table td {
  padding: 8px 4px;
  vertical-align: middle;
  font-size: var(--font-size-md);
}

.axis-table th:first-child {
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  white-space: nowrap;
}

.row-center th {
  font-weight: var(--font-weight-bold);
}

.wheel-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}

.wheel-icon img {
  height: 100px;
}

.wheel-icon span {
  font-weight: var(--font-weight-bold);
  color: var(--status-error);
}

.wheel-icon.overloaded span {
  color: var(--status-error);
}

.row-bottom {
  border-bottom: 1px solid var(--border-default);
}

.overload {
  color: var(--status-error);
}

#axisNameRow {
  font-weight: var(--font-weight-bold);
}

.row-top th, .row-bottom th {
  font-weight: normal !important;
}

.actions .btn {
  margin: 10px 15px 10px 0;
  background-color: var(--surface-selected);
  cursor: pointer;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  font-weight: var(--font-weight-regular);
  padding: 0;
  border: 0;
  font-size: var(--font-size-sm);
}

.actions .timer {
  color: var(--text-secondary);
}

.middle-axis-value {
  font-size: var(--font-size-md);
}

#axisTopRow, #axisBottomRow {
  font-size: var(--font-size-md);
}

.buttons {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
}

.dropdown-wrapper {
  position: relative;
  display: inline-block;
  overflow: visible;
  z-index: 1000;
}

.dropdown-wrapper > button.btn {
  display: block;
  padding: 10px 0 10px 0;
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-55%);
  margin: 0.25rem 0;
  padding: 0;
  list-style: none;
  background: var(--surface-primary);
  border: 1px solid var(--border-default);
  box-shadow: var(--shadow-panel);
  white-space: nowrap;
  z-index: 2000;
}

.dropdown-list li {
  padding: 8px 12px;
  cursor: pointer;
  white-space: nowrap;
}
.dropdown-list li:hover {
  background: var(--surface-secondary);
}

/* Informacja zwrotna o aktualnej treści znaku (zad. 4) */
.vms-displayed-by {
  margin-top: 4px;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.vms-override-badge {
  padding: 1px 6px;
  border-radius: var(--control-radius);
  background: var(--status-warn-border);
  color: var(--status-warn-strong);
  font-size: var(--font-size-xs);
}

.info .fw-bold {
  font-size: var(--font-size-md);
}

.btn-download {
  border-radius: 4px;
}

.btn-download:hover {
  background-color: var(--border-default);
}
</style>
